import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Button, Card, ProgressBar } from '@lms/ui';
import { MessageSquare, Send, ArrowLeft, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useCreateReflection } from '../hooks/useRegistrations';
import { useProgramEnrollment } from '../hooks/useEnrollments';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';

export function ReflectionDetail() {
  const { t } = useLanguage();
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: enrollment, isLoading: enrollmentLoading } = useProgramEnrollment(programId || '');
  const { mutate: submitReflection, isPending: submitting } = useCreateReflection();

  const [reflection, setReflection] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (enrollmentLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const program = enrollment?.program;

  const handleSubmit = () => {
    if (!user || !programId || !reflection.trim()) return;

    submitReflection(
      {
        userId: user.id,
        programId: programId,
        prompt: t(copy.reflectionDetail.reflectionPrompt),
        response: reflection,
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          setTimeout(() => {
            void navigate(`/programs/${programId}/results`);
          }, 3000);
        },
      }
    );
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 text-center space-y-8 animate-fadeIn">
        <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mx-auto text-success shadow-level1">
          <CheckCircle2 size={48} />
        </div>
        <div className="space-y-4">
          <h1 className="text-4xl font-headline font-bold text-on-surface tracking-tight">
            <Bilingual {...copy.reflectionDetail.submittedTitle} />
          </h1>
          <p className="text-on-surface-variant max-w-sm mx-auto">
            <Bilingual {...copy.reflectionDetail.submittedDesc} />
          </p>
        </div>
        <div className="pt-8">
          <p className="text-xs text-on-surface-variant uppercase tracking-widest font-bold mb-4">
            <Bilingual {...copy.reflectionDetail.redirecting} />
          </p>
          <Loader2 className="animate-spin mx-auto text-primary" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-12 px-4 space-y-10 animate-fadeIn">
      {/* Header Navigation */}
      <Link
        to={`/programs/${programId}`}
        className="inline-flex items-center gap-2 text-on-surface-variant hover:text-primary transition-all no-underline font-body py-2 px-4 bg-surface-container rounded-full border border-on-surface/5"
      >
        <ArrowLeft size={16} />
        <span className="text-xs font-bold uppercase tracking-wider">
          <Bilingual {...copy.reflectionDetail.backToCurriculum} />
        </span>
      </Link>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-xl text-primary">
            <Sparkles size={24} />
          </div>
          <span className="text-xs font-bold text-primary uppercase tracking-widest">
            <Bilingual {...copy.reflectionDetail.finalMilestone} />
          </span>
        </div>
        <h1 className="text-4xl font-headline font-bold text-on-surface tracking-tight leading-tight">
          <Bilingual {...copy.reflectionDetail.title} />
        </h1>
        <p className="text-on-surface-variant text-lg font-body leading-relaxed max-w-2xl">
          {t(copy.reflectionDetail.reflectionIntro).replace('{name}', program?.name || '')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <Card className="p-8 space-y-6 bg-surface-container-lowest border-on-surface/5 shadow-level1 rounded-[2rem]">
            <div className="space-y-2">
              <label className="text-sm font-bold text-on-surface block">
                <Bilingual {...copy.reflectionDetail.mainPointLabel} />
              </label>
              <p className="text-xs text-on-surface-variant italic mb-4">
                <Bilingual {...copy.reflectionDetail.mainPointHint} />
              </p>
              <textarea
                value={reflection}
                onChange={e => setReflection(e.target.value)}
                placeholder={t(copy.reflectionDetail.placeholder)}
                className="w-full min-h-[300px] p-6 rounded-2xl bg-surface-container text-on-surface font-body outline-none focus:ring-4 focus:ring-primary/10 border border-on-surface/5 transition-all resize-none"
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!reflection.trim() || submitting}
              className="w-full h-14 rounded-xl flex items-center justify-center gap-3 group"
            >
              {submitting ? <Loader2 className="animate-spin" /> : <Send size={20} />}
              <Bilingual {...copy.reflectionDetail.submitAndFinish} />
            </Button>
          </Card>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="bg-surface-container-lowest rounded-[2rem] p-6 border border-on-surface/5 shadow-level1 space-y-6">
            <div>
              <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest font-bold mb-4">
                <Bilingual {...copy.reflectionDetail.completionStatus} />
              </p>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-on-surface-variant font-medium">
                    <Bilingual {...copy.reflectionDetail.curriculum} />
                  </span>
                  <span className="text-success font-bold uppercase tracking-tighter">100%</span>
                </div>
                <ProgressBar value={100} variant="success" size="sm" />

                <div className="flex justify-between items-center text-xs pt-2">
                  <span className="text-on-surface-variant font-medium">
                    <Bilingual {...copy.reflectionDetail.finalExam} />
                  </span>
                  <span className="text-success font-bold uppercase tracking-tighter">
                    <Bilingual {...copy.reflectionDetail.passed} />
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs border-t border-on-surface/5 pt-4">
                  <span className="text-on-surface-variant font-medium">
                    <Bilingual {...copy.reflectionDetail.nextStep} />
                  </span>
                  <span className="text-primary font-bold uppercase tracking-tighter text-xs">
                    <Bilingual {...copy.reflectionDetail.reflection} />
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-primary/5 rounded-2xl border border-dashed border-primary/20">
              <div className="flex items-start gap-2 mb-2">
                <MessageSquare size={16} className="text-primary mt-0.5" />
                <span className="text-xs font-bold text-primary uppercase tracking-widest">
                  <Bilingual {...copy.reflectionDetail.mentorNote} />
                </span>
              </div>
              <p className="text-[10px] text-on-surface-variant italic leading-relaxed">
                <Bilingual {...copy.reflectionDetail.mentorNoteText} />
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
